import { useCallback, useMemo } from 'react';
import { FilterSelect, ListInputItem, ListItem, type PopupProps } from '@direct-frontend/components';
import { noop } from '@direct-frontend/stdlib';
import { TreeQuickFilter } from '@adv-frontend/filters';
import { type OpenApiSchemaCategory } from '#shared/api/schema';
import { checkboxesToNodeIds } from './lib/checkboxesToNodeIds';
import { createNodeMap } from './lib/createNodeMap';
import { nodeIdsToCheckboxes } from './lib/nodeIdsToCheckboxes';
import { simulateSingleSelectValue } from './lib/simulateSingleSelectValue';
import { useCategoryGetAll } from '../../api';
import { groupCategoriesByParent } from '../../lib/groupCategoriesByParent';
import styles from './FilterCategories.module.css';

export type FilterCategoriesPresentationProps = {
    categorySlugIds: string;
    setCategorySlugIds: (categorySlugIds: string) => void;
    data: OpenApiSchemaCategory[] | null | undefined;
    singleSelect?: boolean;
    placement?: PopupProps['placement'];
};

export const FilterCategoriesPresentation = ({
    categorySlugIds = '',
    setCategorySlugIds,
    data,
    singleSelect = false,
    placement,
}: FilterCategoriesPresentationProps) => {
    const nodes = useMemo(() => {
        if (data === undefined) {
            return null;
        }

        if (data === null) {
            return [];
        }

        return groupCategoriesByParent(data);
    }, [data]);

    const nodeMap = useMemo(() => createNodeMap(nodes ?? []), [nodes]);

    const checkboxesArr = useMemo(
        () => nodeIdsToCheckboxes(categorySlugIds, nodeMap, singleSelect),
        [categorySlugIds, nodeMap, singleSelect]
    );

    const handleChange = useCallback(
        ({ value }: { value: [string, boolean][] }) => {
            const updatedValue = singleSelect ? simulateSingleSelectValue(nodeMap, checkboxesArr, value) : value;

            const newCategorySlugIds = checkboxesToNodeIds(updatedValue, nodeMap, !singleSelect);

            setCategorySlugIds(newCategorySlugIds);
        },
        [setCategorySlugIds, nodeMap, checkboxesArr, singleSelect]
    );

    // Быстрый фикс. Если у селлера еще нет категорий, то показываем "Ничего не найдено", как в коллекциях
    if (nodes?.length === 0) {
        return (
            <FilterSelect
                pinnedSection={<ListInputItem placeholder="Поиск" onChange={noop} />}
                pinnedSectionAfter={<ListItem>Ничего не найдено</ListItem>}
                items={[]}
                popupWidth={400}
                size="xs"
                color="contour"
                aria-label="Категории"
                placeholder="Категории"
                onChange={noop}
                data-testid="FilterCategories.ExpanderChip"
            />
        );
    }

    return (
        <TreeQuickFilter
            className={styles.popup}
            items={nodes}
            color="contour"
            onChange={handleChange}
            aria-label="Категории"
            title="Категории"
            placement={placement}
            value={checkboxesArr}
            data-testid="FilterCategories"
            size="xs"
            withBadgePlaceholder={false}
        />
    );
};

export const FilterCategories = (props: Omit<FilterCategoriesPresentationProps, 'data'>) => {
    const { data } = useCategoryGetAll({});

    return <FilterCategoriesPresentation {...props} data={data} />;
};
